import {
  BLOB_TRANSFER_ACTIONS,
  BLOB_TRANSFER_ACTIONS_VALUES,
  BLOB_TRANSFER_STATUS_VALUES,
  LAMBDA_CONTROL_STATUS_VALUES,
  OPERATION_ENDPOINT_VALUES,
  SCHEDULER_ACTIONS,
  SCHEDULER_ACTIONS_VALUES,
  SCHEDULER_STATUS_VALUES,
  SYNC_ACCOUNT_SAP_TRANSACTION_TYPE_VALUES,
  SYNC_MATERIAL_DOWNSTREAM_NAME_VALUES,
  SYNC_MATERIAL_OPERATION_VALUES,
  SYNC_MATERIAL_TYPE_VALUES,
} from '@repo/constants'
import { z } from 'zod'

export const ediReprocessParamsSchema = z.object({
  dynamo_id: z.string().min(1),
})

export const lambdaControlParamsSchema = z.object({
  use_case_id: z.string().min(1),
  downstream_name: z.string().min(1),
  status: z.enum(LAMBDA_CONTROL_STATUS_VALUES),
})

export const resendLambdaParamsSchema = z.object({
  use_case_id: z.string().min(1),
  downstream_name: z.string().min(1),
  dynamo_id: z.string().min(1),
})

const schedulerParamsBaseSchema = z.object({
  use_case_id: z.string().min(1),
  scheduler_action: z.enum(SCHEDULER_ACTIONS_VALUES),
  scheduler_status: z.enum(SCHEDULER_STATUS_VALUES),
  scheduler_date: z.string().optional(),
  delayed_by_hours: z.number().optional(),
})

export const schedulerParamsSchema = schedulerParamsBaseSchema.superRefine((data, ctx) => {
  if (data.scheduler_action === SCHEDULER_ACTIONS.DELAYED) {
    if (data.scheduler_date === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_type,
        expected: 'string',
        received: 'undefined',
        path: ['scheduler_date'],
      })
    }
    if (data.delayed_by_hours === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_type,
        expected: 'number',
        received: 'undefined',
        path: ['delayed_by_hours'],
      })
    }
  }
})

const blobTransferParamsBaseSchema = z.object({
  use_case_id: z.literal('EDPUpload'),
  scheduler_action: z.enum(BLOB_TRANSFER_ACTIONS_VALUES),
  edp_name: z.string().min(1),
  scheduler_status: z.enum(BLOB_TRANSFER_STATUS_VALUES),
  scheduler_date: z.string().optional(),
  delayed_by_hours: z.number().optional(),
})

export const blobTransferParamsSchema = blobTransferParamsBaseSchema.superRefine((data, ctx) => {
  if (data.scheduler_action === BLOB_TRANSFER_ACTIONS.DELAYED) {
    if (data.scheduler_date === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_type,
        expected: 'string',
        received: 'undefined',
        path: ['scheduler_date'],
      })
    }
    if (data.delayed_by_hours === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_type,
        expected: 'number',
        received: 'undefined',
        path: ['delayed_by_hours'],
      })
    }
  }
})

export const pliByAccountParamsSchema = z.object({
  sap_erp: z.array(z.string().min(1)).min(1),
})

// Sync operation schemas
export const syncAccountSapParamsSchema = z.object({
  use_case_id: z.literal('SFAccount'),
  downstream_name: z.literal('SAP'),
  sf_id: z.string().min(1),
  transaction_types: z.array(z.enum(SYNC_ACCOUNT_SAP_TRANSACTION_TYPE_VALUES)).min(1),
})

export const syncAccountNodParamsSchema = z.object({
  use_case_id: z.literal('SFAccount'),
  downstream_name: z.literal('NOD'),
  sf_id: z.string().min(1),
})

export const syncOrgHierParamsSchema = z.object({
  use_case_id: z.literal('OrganizationHierarchy'),
  downstream_name: z.literal('NOD'),
  sf_id: z.string().min(1),
})

export const syncMaterialParamsSchema = z.object({
  use_case_id: z.literal('Material'),
  downstream_name: z.enum(SYNC_MATERIAL_DOWNSTREAM_NAME_VALUES),
  material: z.string().min(1),
  material_type: z.enum(SYNC_MATERIAL_TYPE_VALUES),
  operation: z.enum(SYNC_MATERIAL_OPERATION_VALUES),
})

export const syncResourceNodParamsSchema = z.object({
  use_case_id: z.literal('Resource'),
  downstream_name: z.literal('NOD'),
  sf_id: z.string().min(1),
})

export const SYNC_OPERATION_SCHEMAS = {
  syncAccountSap: syncAccountSapParamsSchema,
  syncAccountNod: syncAccountNodParamsSchema,
  syncOrgHier: syncOrgHierParamsSchema,
  syncMaterial: syncMaterialParamsSchema,
  syncResourceNod: syncResourceNodParamsSchema,
} as const

const markDeadFailedOrdersParamsBaseSchema = z.object({
  incident_number: z.string().trim().min(1),
  dynamo_id: z.string().optional(),
  customer_identifier: z.string().optional(),
  po_number: z.string().optional(),
})

export const markDeadFailedOrdersParamsSchema = markDeadFailedOrdersParamsBaseSchema.superRefine(
  (data, ctx) => {
    const hasDynamoId = data.dynamo_id !== undefined && data.dynamo_id.trim() !== ''
    const hasCustomerIdentifier =
      data.customer_identifier !== undefined && data.customer_identifier.trim() !== ''
    const hasPoNumber = data.po_number !== undefined && data.po_number.trim() !== ''

    if (!hasDynamoId) {
      if (!hasCustomerIdentifier) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_type,
          expected: 'string',
          received: 'undefined',
          path: ['customer_identifier'],
        })
      }
      if (!hasPoNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_type,
          expected: 'string',
          received: 'undefined',
          path: ['po_number'],
        })
      }
    }
  },
)

export const OPERATION_SCHEMAS = {
  '/sales-order/edi-reprocess': ediReprocessParamsSchema,
  '/soLambdaControl': lambdaControlParamsSchema,
  '/resend': resendLambdaParamsSchema,
  '/schedule/transfer_pause_resume': z.union([schedulerParamsSchema, blobTransferParamsSchema]),
  '/plecc/process': pliByAccountParamsSchema,
  '/asor/invoke_bcp_job': null,
  '/sync': z.union([
    syncAccountSapParamsSchema,
    syncAccountNodParamsSchema,
    syncOrgHierParamsSchema,
    syncMaterialParamsSchema,
    syncResourceNodParamsSchema,
  ]),
  '/sales-order/mark-dead-failed-orders': markDeadFailedOrdersParamsSchema,
} as const

export type EdiReprocessParams = z.infer<typeof ediReprocessParamsSchema>
export type LambdaControlParams = z.infer<typeof lambdaControlParamsSchema>
export type ResendLambdaParams = z.infer<typeof resendLambdaParamsSchema>
export type SchedulerParams = z.infer<typeof schedulerParamsSchema>
export type BlobTransferParams = z.infer<typeof blobTransferParamsSchema>
export type PliByAccountParams = z.infer<typeof pliByAccountParamsSchema>
export type SyncAccountSapParams = z.infer<typeof syncAccountSapParamsSchema>
export type SyncAccountNodParams = z.infer<typeof syncAccountNodParamsSchema>
export type SyncOrgHierParams = z.infer<typeof syncOrgHierParamsSchema>
export type SyncMaterialParams = z.infer<typeof syncMaterialParamsSchema>
export type SyncResourceNodParams = z.infer<typeof syncResourceNodParamsSchema>
export type MarkDeadFailedOrdersParams = z.infer<typeof markDeadFailedOrdersParamsSchema>

export const executeOperationSchema = z
  .object({
    endpoint: z.enum(OPERATION_ENDPOINT_VALUES),
    params: z.record(z.unknown()),
  })
  .superRefine((data, ctx) => {
    const schema = OPERATION_SCHEMAS[data.endpoint as keyof typeof OPERATION_SCHEMAS]
    if (schema) {
      const result = schema.safeParse(data.params)
      if (!result.success) {
        result.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['params', ...issue.path],
          })
        })
      }
    }
  })

export type ExecuteOperationInput = z.infer<typeof executeOperationSchema>
